import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { FileText, Search, Shield, CheckCircle, Plus, X } from 'lucide-react';
import { DashboardLayout } from './components/layout';
import {
  RecentContractsList,
  QuickActionsSection,
  StatsCard,
} from './components/dashboard';
import {
  ContractsTable,
  ContractsFilters,
  Pagination,
  EmptyState,
  Contract,
  ContractStatus,
  ProcessingModal,
} from './components/contracts';
import { UploadModal } from './components/upload';
import {
  ContractMetadataHeader,
  ArticleSectionTree,
  ContractActions,
  AnalysisResults,
  Article,
  AnalysisResult,
} from './components/contract-detail';
import {
  ComparisonSelectionInterface,
  ComparisonConfig,
  ComparisonStatus,
  ContractOption,
  SideBySideViewer,
  ContractData,
  ClauseMatch,
  UnmatchedClause,
  EmbeddingStats,
} from './components/comparison';
import {
  GapFindingsTable,
  GapFinding,
} from './components/gap-analysis';
import {
  RiskHeatMap,
  ArticleRisk,
  RiskItem,
  GapAnalysisMatrix,
  MatrixCell,
  ArticleInfo,
  GAP_TYPES,
  RegulatoryAlignmentRadar,
  ContractComplianceData,
  ComplianceArea,
  RiskPriorityMatrix,
  RiskFinding,
} from './components/visualizations';
import {
  NaturalLanguageSearch,
  SearchResult,
} from './components/search';
import { FinancialRiskAnalysisPanel } from './components/financial-risk/FinancialRiskAnalysisPanel';
import { ContractAmbiguityAnalysisPanel } from './components/contract-ambiguity/ContractAmbiguityAnalysisPanel';
import { DeadlineClausesPanel } from './components/deadline-clauses/DeadlineClausesPanel';
import { ContractChat, ChatContractOption, ChatHistorySidebar } from './components/chat';
import type { ChatMessage } from './components/chat/ContractChat';
import { PipelineViewer } from './components/pipeline';
import { RegulationsPage, RegulatoryDocDetail } from './components/regulations';
import { RegulatoryComplianceView } from './components/visualizations';
import {
  fetchContracts,
  processContract,
  fetchContractDetail,
  getContractStatus,
  ArticleFromAPI,
  SectionWithClauses,
  ClauseFromAPI,
  fetchGapFindings,
  transformGapFinding,
  analyzeGapsWithAI,
  fetchContractRisks,
  fetchRisksByArticle,
  transformRiskFinding,
  HeatMapArticleRiskFromAPI,
  compareContracts,
  transformSimilarClausePair,
  ContractFromAPI,
  fetchComplianceScores,
  transformComplianceScores,
  fetchTopicSummaries,
  fetchDefinedTerms,
  fetchTopicComparison,
  deleteContract,
  TopicSummaryFromAPI,
  DefinedTermFromAPI,
  TopicComparisonContractFromAPI,
  listChatConversations,
  getChatConversation,
  deleteChatConversation,
  type ConversationSummary,
  type PersistedChatMessage,
  fetchRegulatoryDocuments,
  type RegulatoryDocument,
} from './services/api';
import './App.css';


// Mock contracts data for contracts list page
const mockContracts: Contract[] = [
  {
    id: 'c1a2b3c4-d5e6-f7a8-b9c0-d1e2f3a4b5c6',
    name: 'ASES Contract 2025',
    state: 'PR',
    status: 'ready',
    effective_date: '2025-01-01',
    expiration_date: '2025-12-31',
    version: '2.1',
    created_at: '2024-12-15T10:30:00Z',
    updated_at: '2025-01-20T14:45:00Z',
    analysis_status: 'completed',
    gap_count: 47,
  },
  {
    id: 'd2b3c4d5-e6f7-a8b9-c0d1-e2f3a4b5c6d7',
    name: 'Florida SMMC Contract 2025',
    state: 'FL',
    status: 'ready',
    effective_date: '2025-01-01',
    expiration_date: '2025-12-31',
    version: '1.0',
    created_at: '2024-11-20T09:15:00Z',
    updated_at: '2025-01-15T11:20:00Z',
    analysis_status: 'completed',
    gap_count: 0,
  },
  {
    id: 'e3c4d5e6-f7a8-b9c0-d1e2-f3a4b5c6d7e8',
    name: 'Texas STAR Contract Draft',
    state: 'TX',
    status: 'processing',
    effective_date: '2025-07-01',
    version: '0.5',
    created_at: '2025-01-10T16:00:00Z',
    updated_at: '2025-01-25T08:30:00Z',
  },
  {
    id: 'f4d5e6f7-a8b9-c0d1-e2f3-a4b5c6d7e8f9',
    name: 'California Medi-Cal 2024',
    state: 'CA',
    status: 'structured',
    effective_date: '2024-01-01',
    expiration_date: '2024-12-31',
    version: '3.0',
    created_at: '2024-02-01T12:00:00Z',
    updated_at: '2024-06-15T09:00:00Z',
    analysis_status: 'in_progress',
  },
  {
    id: 'a5e6f7a8-b9c0-d1e2-f3a4-b5c6d7e8f9a0',
    name: 'New York Medicaid Managed Care',
    state: 'NY',
    status: 'uploaded',
    effective_date: '2025-04-01',
    created_at: '2025-01-24T14:30:00Z',
    updated_at: '2025-01-24T14:30:00Z',
  },
];

// Mock analysis results (to be replaced with real API data)
const mockAnalysisResults: AnalysisResult[] = [
  {
    id: 'analysis-1',
    type: 'gap_analysis',
    title: 'ASES vs Florida SMMC Comparison',
    status: 'completed',
    completedAt: '2025-01-20T14:45:00Z',
    comparedWith: 'Florida SMMC Contract 2025',
    totalGaps: 47,
    gapSummary: {
      critical: 3,
      high: 12,
      medium: 24,
      low: 8,
    },
    description: 'Comprehensive gap analysis comparing Puerto Rico ASES contract with Florida SMMC benchmark.',
  },
  {
    id: 'analysis-2',
    type: 'risk_analysis',
    title: 'Compliance Risk Assessment',
    status: 'completed',
    completedAt: '2025-01-18T09:30:00Z',
    totalRisks: 15,
    description: 'Risk identification across all contract articles with severity scoring.',
  },
  {
    id: 'analysis-3',
    type: 'article7_analysis',
    title: 'Article 7 Deep Analysis',
    status: 'in_progress',
    description: 'Detailed analysis of covered benefits with EPSDT compliance review.',
  },
];

// Dashboard content component
interface DashboardContentProps {
  onNavigate: (path: string) => void;
  onViewContract: (contract: Contract) => void;
}

const DashboardContent: React.FC<DashboardContentProps> = ({ onNavigate, onViewContract }) => {
  // State for total contracts count
  const [totalContracts, setTotalContracts] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State for recent contracts
  const [recentContracts, setRecentContracts] = useState<ContractFromAPI[]>([]);
  const [contractsLoading, setContractsLoading] = useState(true);
  const [contractsError, setContractsError] = useState<string | null>(null);

  // Fetch contracts on mount (both count and recent list)
  useEffect(() => {
    const loadContracts = async () => {
      try {
        const response = await fetchContracts({ sortBy: 'created_at', sortOrder: 'desc', limit: 5 });
        setTotalContracts(response.pagination.total);
        setRecentContracts(response.contracts);
        setIsLoading(false);
        setContractsLoading(false);
      } catch (err) {
        console.error('Failed to load contracts:', err);
        const message = err instanceof Error ? err.message : 'Failed to load contracts';
        setError(message);
        setContractsError(message);
        setIsLoading(false);
        setContractsLoading(false);
      }
    };
    loadContracts();
  }, []);

  // State for upload modal
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const handleUpload = () => {
    setIsUploadModalOpen(true);
  };

  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false);
  };

  const handleUploadComplete = async (documentId: string) => {
    console.log('Upload complete, document ID:', documentId);
    setIsUploadModalOpen(false);
    // Refresh contracts list after upload
    try {
      const response = await fetchContracts({ sortBy: 'created_at', sortOrder: 'desc', limit: 5 });
      setTotalContracts(response.pagination.total);
      setRecentContracts(response.contracts);
    } catch (err) {
      console.error('Failed to reload contracts:', err);
    }
  };

  const handleCompare = () => {
    console.log('Compare clicked');
    onNavigate('/analysis');
  };

  const handleAnalyze = () => {
    console.log('Analyze clicked');
    onNavigate('/analysis');
  };

  const handleGenerateReport = () => {
    console.log('Generate report clicked');
    onNavigate('/reports');
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900" data-testid="dashboard-title">Dashboard</h2>
          <p className="mt-1 text-sm text-gray-500" data-testid="dashboard-subtitle">
            Medicaid Contract Analysis Platform - Puerto Rico ASES
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
            data-testid="quick-action-upload"
            onClick={handleUpload}
          >
            <Plus className="h-5 w-5 mr-2" />
            Upload Contract
          </button>
        </div>
      </div>

      {/* Status Banner */}
      {!error && !contractsError && (
        <div className="bg-success-100 border border-success-400 rounded-lg p-4 flex items-center" data-testid="status-banner">
          <CheckCircle className="h-6 w-6 text-success-600 mr-3 flex-shrink-0" />
          <span className="text-success-800 font-medium">System operational - All services running</span>
        </div>
      )}

      {/* Error Banner */}
      {(error || contractsError) && (
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4 flex items-start" data-testid="error-banner">
          <Shield className="h-6 w-6 text-danger-600 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-danger-800 font-medium">Error loading dashboard data</p>
            <p className="text-danger-600 text-sm mt-1">
              {error || contractsError}. Some data may be unavailable.
            </p>
          </div>
        </div>
      )}

      {/* Total Contracts Card */}
      <div data-testid="stats-grid">
        <StatsCard
          title="Total Contracts"
          value={totalContracts}
          subtitle={error ? 'Error loading data' : `${totalContracts} ready for analysis`}
          icon={FileText}
          iconColor="text-secondary-600"
          iconBgColor="bg-secondary-100"
          testId="contracts-count-card"
          loading={isLoading}
        />
      </div>

      {/* Quick Actions and Recent Contracts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <QuickActionsSection
          onUpload={handleUpload}
          onCompare={handleCompare}
          onAnalyze={handleAnalyze}
          onGenerateReport={handleGenerateReport}
        />

        {/* Recent Contracts */}
        <RecentContractsList
          contracts={recentContracts}
          onViewContract={onViewContract}
          onViewAll={() => onNavigate('/contracts')}
          loading={contractsLoading}
        />
      </div>

      {/* Upload Modal */}
      <UploadModal
        isOpen={isUploadModalOpen}
        onClose={handleCloseUploadModal}
        onUploadComplete={handleUploadComplete}
      />
    </div>
  );
};

// Contracts List Page
interface ContractsPageProps {
  onViewContract?: (contract: Contract) => void;
  onNavigate?: (path: string) => void;
}

const ContractsPage: React.FC<ContractsPageProps> = ({ onViewContract, onNavigate }) => {
  // State for contracts data
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalContracts, setTotalContracts] = useState(0);

  // State for filters
  const [searchQuery, setSearchQuery] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // State for sorting
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // State for upload modal
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  // State for processing modal
  const [processingContract, setProcessingContract] = useState<Contract | null>(null);

  // Fetch contracts from API
  const loadContracts = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchContracts({
        page: currentPage,
        limit: itemsPerPage,
        state: stateFilter || undefined,
        status: statusFilter || undefined,
        search: searchQuery || undefined,
        sortBy,
        sortOrder,
      });

      // Map API response to Contract type
      const mappedContracts: Contract[] = response.contracts.map((c) => ({
        id: c.id,
        name: c.name,
        state: c.state,
        status: c.status as ContractStatus,
        effective_date: c.effective_date || '',
        expiration_date: c.expiration_date || undefined,
        version: c.version || undefined,
        created_at: c.created_at,
        updated_at: c.updated_at,
        analysis_status: c.analysis_status,
        gap_count: c.gap_count,
      }));

      setContracts(mappedContracts);
      setTotalContracts(response.pagination.total);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load contracts';
      setError(errorMessage);
      console.error('Error loading contracts:', err);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, itemsPerPage, stateFilter, statusFilter, searchQuery, sortBy, sortOrder]);

  // Load contracts on mount and when filters change
  useEffect(() => {
    loadContracts();
  }, [loadContracts]);

  // Pagination calculations (API handles filtering/sorting, we just display the results)
  const totalItems = totalContracts;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  // Contracts from API are already paginated, filtered, and sorted
  const paginatedContracts = contracts;

  // Handlers
  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setStateFilter('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const handleUpload = () => {
    setIsUploadModalOpen(true);
  };

  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false);
  };

  const handleUploadComplete = (documentId: string) => {
    console.log('Upload complete, document ID:', documentId);
    // Close the modal and refresh the contracts list
    setIsUploadModalOpen(false);
    // Reset to first page to see the new upload
    setCurrentPage(1);
    loadContracts();
  };

  const handleView = (contract: Contract) => {
    console.log('View contract:', contract.id);
    onViewContract?.(contract);
  };

  const handleProcess = async (contract: Contract) => {
    console.log('Process contract:', contract.id);
    // Open the processing modal to show real-time feedback
    setProcessingContract(contract);
    try {
      // Start processing in the background
      await processContract(contract.id);
      // Processing started - the modal will poll for status updates
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to process contract';
      console.error('Process error:', err);
      // Close modal and show error
      setProcessingContract(null);
      alert(`Error starting processing: ${message}`);
    }
  };

  const handleProcessingComplete = (status: ContractStatus) => {
    console.log('Processing complete with status:', status);
    // Refresh the contracts list to show updated status
    loadContracts();
  };

  const handleCloseProcessingModal = () => {
    setProcessingContract(null);
    // Refresh contracts to get latest status
    loadContracts();
  };

  const handleAnalyze = (contract: Contract) => {
    console.log('Analyze contract:', contract.id);
    // Navigate to analysis page
    onNavigate?.('/analysis');
  };

  const handleDelete = async (contract: Contract) => {
    if (!window.confirm(`Delete "${contract.name}" and all associated data? This cannot be undone.`)) {
      return;
    }
    try {
      await deleteContract(contract.id);
      loadContracts();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete contract';
      alert(`Delete failed: ${message}`);
    }
  };

  // Determine empty state type
  const hasFilters = searchQuery || stateFilter || statusFilter;
  const showEmptyState = !isLoading && (contracts.length === 0);
  const emptyStateType = !hasFilters ? 'no-contracts' : 'no-results';

  // Get available filters from data (use common US states as defaults)
  const availableStates = contracts.length > 0
    ? Array.from(new Set(contracts.map((c) => c.state)))
    : ['FL', 'TX', 'CA', 'NY', 'PR'];
  const availableStatuses: ContractStatus[] = ['uploaded', 'processing', 'structured', 'ready'];

  return (
    <>
      <div className="space-y-6" data-testid="contracts-page">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900" data-testid="contracts-title">
              Contracts
            </h2>
            <p className="mt-1 text-sm text-gray-500" data-testid="contracts-subtitle">
              Manage and analyze your Medicaid contracts
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <button
              onClick={handleUpload}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
              data-testid="upload-contract-button"
            >
              <Plus className="h-5 w-5 mr-2" />
              Upload Contract
            </button>
          </div>
        </div>

      {/* Filters */}
      <ContractsFilters
        searchQuery={searchQuery}
        onSearchChange={(query) => {
          setSearchQuery(query);
          setCurrentPage(1);
        }}
        stateFilter={stateFilter}
        onStateFilterChange={(state) => {
          setStateFilter(state);
          setCurrentPage(1);
        }}
        statusFilter={statusFilter}
        onStatusFilterChange={(status) => {
          setStatusFilter(status);
          setCurrentPage(1);
        }}
        availableStates={availableStates}
        availableStatuses={availableStatuses}
        onClearFilters={handleClearFilters}
      />

      {/* Results summary */}
      {!showEmptyState && !isLoading && (
        <div className="text-sm text-gray-500" data-testid="results-summary">
          {hasFilters
            ? `Found ${totalContracts} contract${totalContracts !== 1 ? 's' : ''} matching your filters`
            : `${totalContracts} contract${totalContracts !== 1 ? 's' : ''} total`}
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12" data-testid="loading-state">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-3 text-gray-500">Loading contracts...</span>
        </div>
      )}

      {/* Error state */}
      {error && !isLoading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="error-state">
          <p className="text-red-700">{error}</p>
          <button
            onClick={loadContracts}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Try again
          </button>
        </div>
      )}

      {/* Content */}
      {!isLoading && !error && (
        showEmptyState ? (
          <EmptyState
            type={emptyStateType}
            onUpload={handleUpload}
            onClearFilters={hasFilters ? handleClearFilters : undefined}
          />
        ) : (
          <>
            <ContractsTable
              contracts={paginatedContracts}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              onView={handleView}
              onProcess={handleProcess}
              onAnalyze={handleAnalyze}
              onDelete={handleDelete}
            />

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            )}
          </>
        )
      )}
      </div>

      {/* Upload Modal */}
      <UploadModal
        isOpen={isUploadModalOpen}
        onClose={handleCloseUploadModal}
        onUploadComplete={handleUploadComplete}
      />

      {/* Processing Modal */}
      {processingContract && (
        <ProcessingModal
          isOpen={!!processingContract}
          contractId={processingContract.id}
          contractName={processingContract.name}
          initialStatus={processingContract.status}
          onClose={handleCloseProcessingModal}
          onComplete={handleProcessingComplete}
        />
      )}
    </>
  );
};

// Standard article references for gap analysis matrix (these are the common articles in Medicaid contracts)
const mockMatrixArticles: ArticleInfo[] = [
  { id: 'art-1', number: '1', title: 'Definitions and Interpretations' },
  { id: 'art-7', number: '7', title: 'Covered Benefits and Services' },
  { id: 'art-11', number: '11', title: 'Utilization Management' },
  { id: 'art-6', number: '6', title: 'Grievance and Appeals' },
  { id: 'art-4', number: '4', title: 'Provider Network' },
  { id: 'art-8', number: '8', title: 'Care Management' },
];

// Helper function to transform API article/section/clause structure to ContractData format
function transformContractToViewerFormat(
  contractId: string,
  contractName: string,
  state: string,
  articles: ArticleFromAPI[]
): ContractData {
  return {
    id: contractId,
    name: contractName,
    state,
    articles: articles.map((article) => ({
      id: article.id,
      number: article.number,
      title: article.title,
      sections: article.sections.map((section: SectionWithClauses) => ({
        id: section.id,
        number: section.number,
        title: section.title,
        clauses: (section.clauses || []).map((clause: ClauseFromAPI) => ({
          id: clause.id,
          number: clause.number,
          text: clause.text,
          hasCfrCitation: clause.metadata?.cfr_citation ? true : false,
          hasRisk: clause.metadata?.has_risk || false,
          riskSeverity: clause.metadata?.risk_severity,
        })),
      })),
      riskCount: article.sections.reduce(
        (acc: number, section: SectionWithClauses) =>
          acc + (section.clauses || []).filter((c: ClauseFromAPI) => c.metadata?.has_risk).length,
        0
      ),
    })),
  };
}

// Analysis Page with Comparison Selection Interface, Side-by-Side Viewer, and Gap Findings
const AnalysisPage: React.FC = () => {
  const [comparisonStatus, setComparisonStatus] = useState<ComparisonStatus>('idle');
  const [comparisonProgress, setComparisonProgress] = useState(0);
  const [currentComparisonStep, setCurrentComparisonStep] = useState<string | undefined>();
  const [comparisonError, setComparisonError] = useState<string | undefined>();
  const [showResults, setShowResults] = useState(false);
  const [activeTab, setActiveTab] = useState<'viewer' | 'gaps' | 'matrix' | 'topics' | 'terms'>('gaps');

  // Comparison results state - fetched from API
  const [sourceContractData, setSourceContractData] = useState<ContractData | null>(null);
  const [targetContractData, setTargetContractData] = useState<ContractData | null>(null);
  const [clauseMatches, setClauseMatches] = useState<ClauseMatch[]>([]);
  const [unmatchedClauses, setUnmatchedClauses] = useState<{
    sourceUnmatched: UnmatchedClause[];
    targetUnmatched: UnmatchedClause[];
    totalSourceUnmatched: number;
    totalTargetUnmatched: number;
  } | undefined>(undefined);
  const [embeddingStats, setEmbeddingStats] = useState<EmbeddingStats | undefined>(undefined);
  const [comparisonConfig, setComparisonConfig] = useState<ComparisonConfig | null>(null);
  const [topicSummaries, setTopicSummaries] = useState<TopicSummaryFromAPI[]>([]);
  const [definedTerms, setDefinedTerms] = useState<DefinedTermFromAPI[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<string>('');
  const [topicComparison, setTopicComparison] = useState<TopicComparisonContractFromAPI[]>([]);

  // Gap findings state - fetched from API
  const [gapFindings, setGapFindings] = useState<GapFinding[]>([]);
  const [isLoadingGaps, setIsLoadingGaps] = useState(false);
  const [gapError, setGapError] = useState<string | undefined>();

  // Contract options state - fetched from API
  const [contractOptions, setContractOptions] = useState<ContractOption[]>([]);
  const [isLoadingContracts, setIsLoadingContracts] = useState(true);
  const [contractsError, setContractsError] = useState<string | undefined>();

  // Generate matrix cells from gap findings dynamically
  const matrixCells = useMemo((): MatrixCell[] => {
    const cells: MatrixCell[] = [];

    // Group gaps by article and type
    const gapsByArticleAndType: Record<string, Record<string, GapFinding[]>> = {};

    gapFindings.forEach(gap => {
      // Extract article number from source_clause_reference
      const match = gap.source_clause_reference.match(/Article (\d+)/);
      const articleNum = match ? match[1] : '1';
      const articleId = `art-${articleNum}`;

      if (!gapsByArticleAndType[articleId]) {
        gapsByArticleAndType[articleId] = {};
      }
      if (!gapsByArticleAndType[articleId][gap.gap_type]) {
        gapsByArticleAndType[articleId][gap.gap_type] = [];
      }
      gapsByArticleAndType[articleId][gap.gap_type].push(gap);
    });

    // Create cells for each article x gap type combination
    mockMatrixArticles.forEach(article => {
      GAP_TYPES.forEach(gapType => {
        const gaps = gapsByArticleAndType[article.id]?.[gapType] || [];
        const severityCounts = { critical: 0, high: 0, medium: 0, low: 0 };

        gaps.forEach(gap => {
          severityCounts[gap.severity]++;
        });

        cells.push({
          articleId: article.id,
          articleNumber: article.number,
          articleTitle: article.title,
          gapType: gapType,
          gapCount: gaps.length,
          gaps: gaps.map(g => ({
            id: g.id,
            severity: g.severity,
            description: g.description,
          })),
          severityCounts,
        });
      });
    });

    return cells;
  }, [gapFindings]);

  // Fetch contracts from API on mount
  useEffect(() => {
    const loadContracts = async () => {
      setIsLoadingContracts(true);
      setContractsError(undefined);
      try {
        const response = await fetchContracts({ limit: 100 });
        const options: ContractOption[] = response.contracts.map((c) => ({
          id: c.id,
          name: c.name,
          state: c.state,
          status: c.status as ContractStatus,
          version: c.version ?? undefined,
          effective_date: c.effective_date ?? undefined,
        }));
        console.log('[AnalysisPage] Loaded', options.length, 'contracts from API:', options.map(c => ({ id: c.id, name: c.name, status: c.status })));
        setContractOptions(options);
      } catch (error) {
        console.error('[AnalysisPage] Failed to load contracts:', error);
        setContractsError(error instanceof Error ? error.message : 'Failed to load contracts');
      } finally {
        setIsLoadingContracts(false);
      }
    };
    loadContracts();
  }, []);

  // Fetch gap findings from API
  const loadGapFindings = useCallback(async (options?: { analysisId?: string }) => {
    setIsLoadingGaps(true);
    setGapError(undefined);

    try {
      const response = await fetchGapFindings({
        limit: 100,
        analysisId: options?.analysisId,
      });
      const transformedFindings = response.data.map(transformGapFinding);
      setGapFindings(transformedFindings);
      console.log('[AnalysisPage] Loaded', transformedFindings.length, 'gap findings from API', options?.analysisId ? `(analysisId: ${options.analysisId})` : '');
    } catch (error) {
      console.error('[AnalysisPage] Failed to fetch gap findings:', error);
      setGapError(error instanceof Error ? error.message : 'Failed to load gap findings');
      setGapFindings([]);
    } finally {
      setIsLoadingGaps(false);
    }
  }, []);

  const runComparison = useCallback(async (config: ComparisonConfig) => {
    try {
      // Step 1: Loading contract data
      setComparisonStatus('preparing');
      setCurrentComparisonStep('Loading contract data...');
      setComparisonProgress(10);

      console.log('[AnalysisPage] Running comparison with config:', config);
      console.log('[AnalysisPage] Source contract ID:', config.sourceContractId);
      console.log('[AnalysisPage] Target contract ID:', config.targetContractId);

      // Fetch both contract details - handle errors individually for better debugging
      let sourceDetail, targetDetail;
      try {
        sourceDetail = await fetchContractDetail(config.sourceContractId);
      } catch (error) {
        console.error('[AnalysisPage] Failed to fetch source contract:', config.sourceContractId, error);
        throw new Error(`Source contract not found (ID: ${config.sourceContractId})`);
      }

      try {
        targetDetail = await fetchContractDetail(config.targetContractId);
      } catch (error) {
        console.error('[AnalysisPage] Failed to fetch target contract:', config.targetContractId, error);
        throw new Error(`Target contract not found (ID: ${config.targetContractId})`);
      }

      // Transform to viewer format
      const sourceData = transformContractToViewerFormat(
        sourceDetail.contract.id,
        sourceDetail.contract.name,
        sourceDetail.contract.state,
        sourceDetail.articles
      );
      const targetData = transformContractToViewerFormat(
        targetDetail.contract.id,
        targetDetail.contract.name,
        targetDetail.contract.state,
        targetDetail.articles
      );

      setSourceContractData(sourceData);
      setTargetContractData(targetData);
      setComparisonProgress(30);

      // Step 2: Finding similar clauses
      setComparisonStatus('comparing');
      setCurrentComparisonStep('Finding similar clauses across contracts...');
      setComparisonProgress(50);

      // Call comparison API
      const comparisonResult = await compareContracts({
        sourceContractId: config.sourceContractId,
        targetContractId: config.targetContractId,
        similarityThreshold: 0.5, // Lower threshold to get more matches
        maxSimilarPairs: 5000, // Increased to cover full contracts
        includeStructural: true,
      });

      // Transform similar clauses to ClauseMatch format
      const matches = comparisonResult.semantic.similarClauses.map(transformSimilarClausePair);
      setClauseMatches(matches);
      console.log('[AnalysisPage] Found', matches.length, 'clause matches');

      // Store unmatched clauses from comparison result
      if (comparisonResult.unmatchedClauses) {
        setUnmatchedClauses(comparisonResult.unmatchedClauses);
        console.log('[AnalysisPage] Unmatched clauses - Source:', comparisonResult.unmatchedClauses.totalSourceUnmatched, ', Target:', comparisonResult.unmatchedClauses.totalTargetUnmatched);
      } else {
        setUnmatchedClauses(undefined);
      }

      // Store embedding stats if available (from API response)
      if (comparisonResult.embeddingStats) {
        setEmbeddingStats(comparisonResult.embeddingStats);
      } else {
        setEmbeddingStats(undefined);
      }

      setComparisonProgress(60);

      // Step 3: Analyze gaps with Claude AI (NEW - this creates gap findings)
      setComparisonStatus('analyzing');
      setCurrentComparisonStep('Analyzing gaps with AI...');

      let gapAnalysisId: string | undefined;
      try {
        const gapAnalysisResult = await analyzeGapsWithAI({
          sourceContractId: config.sourceContractId,
          targetContractId: config.targetContractId,
          similarityThreshold: 0.5,
          maxPairs: 100,
        });
        console.log('[AnalysisPage] Gap analysis complete:', gapAnalysisResult);
        gapAnalysisId = gapAnalysisResult.analysisId;
      } catch (gapError) {
        // Log but don't fail the whole comparison if gap analysis fails
        console.warn('[AnalysisPage] Gap analysis failed (continuing without AI analysis):', gapError);
      }

      setComparisonProgress(80);

      // Step 4: Fetch the generated gap findings
      setCurrentComparisonStep('Loading gap findings...');

      await loadGapFindings({ analysisId: gapAnalysisId });

      setComparisonProgress(100);
      setComparisonStatus('completed');
      setCurrentComparisonStep(undefined);
      setShowResults(true);
    } catch (error) {
      console.error('[AnalysisPage] Comparison failed:', error);
      setComparisonError(error instanceof Error ? error.message : 'Failed to run comparison');
      setComparisonStatus('error');
    }
  }, [loadGapFindings]);

  const handleStartComparison = (config: ComparisonConfig) => {
    console.log('Starting comparison with config:', config);
    setComparisonConfig(config);
    setComparisonError(undefined);
    setShowResults(false);
    setSourceContractData(null);
    setTargetContractData(null);
    setClauseMatches([]);
    setUnmatchedClauses(undefined);
    setEmbeddingStats(undefined);
    runComparison(config);
  };

  const handleCancelComparison = () => {
    setComparisonStatus('idle');
    setComparisonProgress(0);
    setCurrentComparisonStep(undefined);
    setComparisonError(undefined);
    setShowResults(false);
  };

  useEffect(() => {
    if (!showResults) return;
    fetchTopicSummaries(12).then(setTopicSummaries).catch(() => setTopicSummaries([]));
    fetchDefinedTerms(12).then(setDefinedTerms).catch(() => setDefinedTerms([]));
  }, [showResults]);

  useEffect(() => {
    if (!selectedTopic) {
      setTopicComparison([]);
      return;
    }
    fetchTopicComparison(selectedTopic).then(setTopicComparison).catch(() => setTopicComparison([]));
  }, [selectedTopic]);

  const handleRetryComparison = () => {
    if (comparisonConfig) {
      setComparisonError(undefined);
      setShowResults(false);
      setSourceContractData(null);
      setTargetContractData(null);
      setClauseMatches([]);
      setUnmatchedClauses(undefined);
      setEmbeddingStats(undefined);
      runComparison(comparisonConfig);
    }
  };

  const handleClauseClick = (clause: any, section: any, article: any, contractId: string) => {
    console.log('Clause clicked:', clause.id, 'in contract:', contractId);
  };

  const handleGapFindingClick = (finding: GapFinding) => {
    console.log('Gap finding clicked:', finding.id);
  };

  const handleExportCSV = () => {
    console.log('Exporting CSV...');
    // The GapFindingsTable handles the actual export
  };

  return (
    <div className="space-y-6" data-testid="analysis-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900" data-testid="analysis-title">Analysis</h2>
          <p className="mt-1 text-sm text-gray-500" data-testid="analysis-subtitle">
            Run gap analysis and compare Medicaid contracts
          </p>
        </div>
      </div>

      <ComparisonSelectionInterface
        contracts={contractOptions}
        onStartComparison={handleStartComparison}
        comparisonStatus={comparisonStatus}
        comparisonProgress={comparisonProgress}
        currentComparisonStep={currentComparisonStep}
        comparisonError={comparisonError}
        onCancelComparison={handleCancelComparison}
        onRetryComparison={handleRetryComparison}
        isLoadingContracts={isLoadingContracts}
        contractsError={contractsError}
        testId="comparison-selection"
      />

      {/* Results tabs - shown after comparison completes */}
      {showResults && comparisonStatus === 'completed' && (
        <div className="space-y-4">
          {/* Tab navigation */}
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px space-x-8" data-testid="results-tabs">
              <button
                onClick={() => setActiveTab('gaps')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'gaps'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                data-testid="tab-gap-findings"
              >
                Gap Findings
                <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">
                  {gapFindings.length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('matrix')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'matrix'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                data-testid="tab-gap-matrix"
              >
                Gap Matrix
              </button>
              <button
                onClick={() => setActiveTab('topics')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'topics'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                data-testid="tab-topics"
              >
                Topics
              </button>
              <button
                onClick={() => setActiveTab('terms')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'terms'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                data-testid="tab-defined-terms"
              >
                Defined Terms
              </button>
              <button
                onClick={() => setActiveTab('viewer')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'viewer'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                data-testid="tab-side-by-side"
              >
                Side-by-Side Comparison
              </button>
            </nav>
          </div>

          {/* Tab content */}
          {activeTab === 'gaps' && (
            <>
              {gapError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                  <strong>Error:</strong> {gapError}
                </div>
              )}
              <GapFindingsTable
                findings={gapFindings}
                sourceContractName={sourceContractData?.name || 'Source Contract'}
                targetContractName={targetContractData?.name || 'Target Contract'}
                onFindingClick={handleGapFindingClick}
                onExportCSV={handleExportCSV}
                isLoading={isLoadingGaps}
                testId="gap-findings-table"
              />
            </>
          )}

          {activeTab === 'matrix' && (
            <GapAnalysisMatrix
              cells={matrixCells}
              articles={mockMatrixArticles}
              onCellClick={(cell) => {
                console.log('Cell clicked:', cell.articleId, cell.gapType, cell.gapCount);
              }}
              onDrillDown={(cell) => {
                console.log('Drill down to gaps:', cell.articleId, cell.gapType);
                // In production, this would filter the gap findings table and switch tabs
                setActiveTab('gaps');
              }}
              testId="gap-analysis-matrix"
            />
          )}


          {activeTab === 'topics' && (
            <div className="bg-white rounded-lg border border-gray-200 p-4" data-testid="topics-tab-content">
              <h3 className="text-base font-semibold text-gray-900 mb-3">Topic Coverage</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  {topicSummaries.map((topic) => (
                    <button
                      key={topic.topic}
                      onClick={() => setSelectedTopic(topic.topic)}
                      className="w-full text-left border rounded px-3 py-2 mb-2 hover:border-primary-400"
                      data-testid={`topic-item-${topic.topic}`}
                    >
                      <div className="font-medium">{topic.topic}</div>
                      <div className="text-xs text-gray-500">{topic.chunkCount} chunks • {topic.contractCount} contracts</div>
                    </button>
                  ))}
                </div>
                <div data-testid="topic-comparison-panel">
                  <div className="text-sm font-medium mb-2">{selectedTopic ? `Compare topic: ${selectedTopic}` : 'Select a topic'}</div>
                  {topicComparison.map((row) => (
                    <div key={row.contractId} className="border rounded p-2 mb-2">
                      <div className="font-medium text-sm">{row.contractName} ({row.state})</div>
                      <div className="text-xs text-gray-500">{row.chunkCount} chunks • {row.topSections.join(', ')}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'terms' && (
            <div className="bg-white rounded-lg border border-gray-200 p-4" data-testid="terms-tab-content">
              <h3 className="text-base font-semibold text-gray-900 mb-3">Defined Terms</h3>
              <div className="space-y-2">
                {definedTerms.map((term) => (
                  <div key={term.normalized} className="border rounded px-3 py-2" data-testid={`defined-term-${term.normalized}`}>
                    <div className="font-medium">{term.term}</div>
                    <div className="text-xs text-gray-500">{term.chunkCount} mentions across {term.contractCount} contracts</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'viewer' && (
            <SideBySideViewer
              sourceContract={sourceContractData}
              targetContract={targetContractData}
              clauseMatches={clauseMatches}
              unmatchedClauses={unmatchedClauses}
              embeddingStats={embeddingStats}
              onClauseClick={handleClauseClick}
              onEmbeddingsGenerated={handleRetryComparison}
              testId="side-by-side-viewer"
            />
          )}
        </div>
      )}
    </div>
  );
};

interface RisksPageProps {
  onNavigate: (path: string) => void;
  onViewContractClause: (contractId: string, clauseReference: string) => void;
}

// Contract colors for radar chart
const CONTRACT_COLORS = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b'];

const RisksPage: React.FC<RisksPageProps> = ({ onNavigate, onViewContractClause }) => {
  const [activeTab, setActiveTab] = useState<'heatmap' | 'radar' | 'priority' | 'doc-compliance'>('heatmap');
  const [selectedArticle, setSelectedArticle] = useState<ArticleRisk | null>(null);

  // Risk data state - fetched from API
  const [articleRisks, setArticleRisks] = useState<ArticleRisk[]>([]);
  const [riskFindings, setRiskFindings] = useState<RiskFinding[]>([]);
  const [isLoadingRisks, setIsLoadingRisks] = useState(false);
  const [riskError, setRiskError] = useState<string | undefined>();

  // Compliance data state - fetched from API
  const [complianceData, setComplianceData] = useState<ContractComplianceData[]>([]);
  const [isLoadingCompliance, setIsLoadingCompliance] = useState(false);

  // Load risk data on mount
  useEffect(() => {
    const loadRiskData = async () => {
      setIsLoadingRisks(true);
      setRiskError(undefined);

      try {
        // Use a sample contract ID for now - in production this would come from context or route params
        const contractId = 'c1a2b3c4-d5e6-f7a8-b9c0-d1e2f3a4b5c6'; // Sample contract ID

        // Fetch risks by article (for heat map) and contract risks (for priority matrix) in parallel
        const [risksByArticleResponse, risksResponse] = await Promise.all([
          fetchRisksByArticle(contractId).catch(() => null),
          fetchContractRisks(contractId).catch(() => null),
        ]);

        // Set article risks for heat map (already in correct format)
        if (risksByArticleResponse && risksByArticleResponse.articles.length > 0) {
          // Transform API response to frontend ArticleRisk format
          const transformedArticles: ArticleRisk[] = risksByArticleResponse.articles.map(
            (article: HeatMapArticleRiskFromAPI) => ({
              articleId: article.articleId,
              articleNumber: article.articleNumber,
              articleTitle: article.articleTitle,
              risks: article.risks.map((risk) => ({
                id: risk.id,
                description: risk.description,
                severity: risk.severity,
                cfrCitation: risk.cfrCitation,
                clauseReference: risk.clauseReference,
              })),
              riskCounts: article.riskCounts,
              overallSeverity: article.overallSeverity,
            })
          );
          setArticleRisks(transformedArticles);
          console.log('[RisksPage] Loaded', transformedArticles.length, 'articles with risks from API');
        } else {
          setArticleRisks([]);
          console.log('[RisksPage] No article risks available');
        }

        // Transform and set risk findings for priority matrix
        if (risksResponse && risksResponse.risks.length > 0) {
          const transformedRisks = risksResponse.risks.map((risk) =>
            transformRiskFinding(risk, contractId)
          );
          setRiskFindings(transformedRisks);
          console.log('[RisksPage] Loaded', transformedRisks.length, 'risk findings from API');
        } else {
          setRiskFindings([]);
          console.log('[RisksPage] No risk findings available');
        }
      } catch (error) {
        console.error('[RisksPage] Failed to fetch risk data:', error);
        setRiskError(error instanceof Error ? error.message : 'Failed to load risk data');
        setArticleRisks([]);
        setRiskFindings([]);
      } finally {
        setIsLoadingRisks(false);
      }
    };

    loadRiskData();
  }, []);

  // Load compliance data for radar chart when the radar tab is active
  useEffect(() => {
    if (activeTab !== 'radar') return;

    const loadComplianceData = async () => {
      setIsLoadingCompliance(true);

      try {
        // Fetch compliance scores for multiple contracts
        // In production, this would use contract IDs from route params or context
        const contractIds = [
          'c1a2b3c4-d5e6-f7a8-b9c0-d1e2f3a4b5c6',
          'd2b3c4d5-e6f7-a8b9-c0d1-e2f3a4b5c6d7',
        ];

        const compliancePromises = contractIds.map((id, index) =>
          fetchComplianceScores(id)
            .then((response) => transformComplianceScores(response, CONTRACT_COLORS[index % CONTRACT_COLORS.length]))
            .catch((error) => {
              console.warn(`[RisksPage] Failed to fetch compliance for contract ${id}:`, error);
              return null;
            })
        );

        const results = await Promise.all(compliancePromises);
        const validResults = results.filter((r) => r !== null) as ContractComplianceData[];

        setComplianceData(validResults);
        console.log('[RisksPage] Loaded compliance data for', validResults.length, 'contracts');
      } catch (error) {
        console.error('[RisksPage] Failed to fetch compliance data:', error);
        setComplianceData([]);
      } finally {
        setIsLoadingCompliance(false);
      }
    };

    // Only load if we don't have compliance data yet
    if (complianceData.length === 0) {
      loadComplianceData();
    }
  }, [activeTab, complianceData.length]);

  const handleArticleClick = (article: ArticleRisk) => {
    setSelectedArticle(article);
  };

  const handleRiskClick = (risk: RiskItem, article: ArticleRisk) => {
    console.log('Risk clicked:', risk.id, 'in article:', article.articleNumber);
  };

  const handleRadarAreaClick = (area: ComplianceArea, contract: ContractComplianceData) => {
    console.log('Radar area clicked:', area.key, 'for contract:', contract.state);
  };

  const handleViewGaps = (area: ComplianceArea, contract: ContractComplianceData) => {
    console.log('View gaps for:', area.key, 'in contract:', contract.state);
  };

  const handleViewInContract = (risk: RiskFinding) => {
    // Navigate to the specific contract and highlight the relevant clause
    if (risk.contractId && risk.clauseReference) {
      onViewContractClause(risk.contractId, risk.clauseReference);
    } else {
      // Fallback to contracts list if no contract ID
      onNavigate('/contracts');
    }
  };

  const handlePriorityRiskClick = (risk: RiskFinding) => {
    console.log('Priority risk clicked:', risk.id, risk.description);
  };

  return (
    <div className="space-y-6" data-testid="risks-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900" data-testid="risks-title">Risks & Compliance</h2>
          <p className="mt-1 text-sm text-gray-500" data-testid="risks-subtitle">
            View identified compliance risks and regulatory alignment across contracts.
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex -mb-px space-x-8" data-testid="risks-tabs">
          <button
            onClick={() => setActiveTab('heatmap')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'heatmap'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            data-testid="tab-risk-heatmap"
          >
            Risk Heat Map
          </button>
          <button
            onClick={() => setActiveTab('radar')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'radar'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            data-testid="tab-regulatory-radar"
          >
            Regulatory Alignment
          </button>
          <button
            onClick={() => setActiveTab('priority')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'priority'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            data-testid="tab-priority-matrix"
          >
            Priority Matrix
          </button>
          <button
            onClick={() => setActiveTab('doc-compliance')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'doc-compliance'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            data-testid="tab-doc-compliance"
          >
            Document Compliance
          </button>
        </nav>
      </div>

      {/* Loading indicator */}
      {isLoadingRisks && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-3 text-gray-600">Loading risk data...</span>
        </div>
      )}

      {/* Error message */}
      {riskError && !isLoadingRisks && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
          <strong>Error:</strong> {riskError}
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'heatmap' && !isLoadingRisks && (
        <>
          {/* Show prominent empty state when no articles with risks */}
          {articleRisks.length === 0 && !riskError && (
            <EmptyState
              type="no-risks"
              onRunAnalysis={() => setActiveTab('doc-compliance')}
              onViewContracts={() => onNavigate('/contracts')}
            />
          )}

          {/* Risk Heat Map - only show when we have data */}
          {articleRisks.length > 0 && (
            <RiskHeatMap
              articles={articleRisks}
              onArticleClick={handleArticleClick}
              onRiskClick={handleRiskClick}
              testId="risk-heatmap"
            />
          )}

          {/* Article Risk Details Panel */}
          {selectedArticle && (
            <div className="bg-white rounded-lg shadow-md p-6" data-testid="article-risk-details">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Article {selectedArticle.articleNumber}: {selectedArticle.articleTitle}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedArticle.risks.length} risk{selectedArticle.risks.length !== 1 ? 's' : ''} identified
                  </p>
                </div>
                <button
                  onClick={() => setSelectedArticle(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  data-testid="close-article-details"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>

              {/* Risk counts by severity */}
              <div className="grid grid-cols-4 gap-3 mb-6">
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-red-600">{selectedArticle.riskCounts.critical}</div>
                  <div className="text-xs text-red-500">Critical</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-orange-600">{selectedArticle.riskCounts.high}</div>
                  <div className="text-xs text-orange-500">High</div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-yellow-600">{selectedArticle.riskCounts.medium}</div>
                  <div className="text-xs text-yellow-500">Medium</div>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-600">{selectedArticle.riskCounts.low}</div>
                  <div className="text-xs text-green-500">Low</div>
                </div>
              </div>

              {/* Risk list */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-700">Risk Details</h4>
                {selectedArticle.risks.length > 0 ? (
                  <ul className="divide-y divide-gray-100">
                    {selectedArticle.risks.map((risk) => (
                      <li key={risk.id} className="py-3 first:pt-0 last:pb-0" data-testid={`risk-detail-${risk.id}`}>
                        <div className="flex items-start gap-3">
                          <span className={`flex-shrink-0 mt-1 w-2 h-2 rounded-full ${
                            risk.severity === 'critical' ? 'bg-red-500' :
                            risk.severity === 'high' ? 'bg-orange-500' :
                            risk.severity === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                          }`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-900">{risk.description}</p>
                            <div className="flex items-center gap-4 mt-1">
                              {risk.cfrCitation && (
                                <span className="text-xs text-gray-500">
                                  CFR: {risk.cfrCitation}
                                </span>
                              )}
                              {risk.clauseReference && (
                                <span className="text-xs text-gray-500">
                                  Clause: {risk.clauseReference}
                                </span>
                              )}
                              <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                                risk.severity === 'critical' ? 'bg-red-100 text-red-700' :
                                risk.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                                risk.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                              }`}>
                                {risk.severity.charAt(0).toUpperCase() + risk.severity.slice(1)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No risks identified for this article.</p>
                )}
              </div>
            </div>
          )}

          {/* Risk Summary Stats - dynamically calculated from articleRisks - only show when we have data */}
          {articleRisks.length > 0 && (() => {
            const riskCounts = articleRisks.reduce(
              (acc, article) => ({
                critical: acc.critical + article.riskCounts.critical,
                high: acc.high + article.riskCounts.high,
                medium: acc.medium + article.riskCounts.medium,
                low: acc.low + article.riskCounts.low,
              }),
              { critical: 0, high: 0, medium: 0, low: 0 }
            );
            return (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-500" data-testid="critical-risks-card">
                  <div className="text-sm font-medium text-gray-500">Critical Risks</div>
                  <div className="mt-2 text-3xl font-bold text-red-600">{riskCounts.critical}</div>
                  <div className="mt-1 text-xs text-gray-400">Requires immediate attention</div>
                </div>
                <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500" data-testid="high-risks-card">
                  <div className="text-sm font-medium text-gray-500">High Risks</div>
                  <div className="mt-2 text-3xl font-bold text-orange-600">{riskCounts.high}</div>
                  <div className="mt-1 text-xs text-gray-400">Address within 30 days</div>
                </div>
                <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-yellow-500" data-testid="medium-risks-card">
                  <div className="text-sm font-medium text-gray-500">Medium Risks</div>
                  <div className="mt-2 text-3xl font-bold text-yellow-600">{riskCounts.medium}</div>
                  <div className="mt-1 text-xs text-gray-400">Monitor and plan</div>
                </div>
                <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500" data-testid="low-risks-card">
                  <div className="text-sm font-medium text-gray-500">Low Risks</div>
                  <div className="mt-2 text-3xl font-bold text-green-600">{riskCounts.low}</div>
                  <div className="mt-1 text-xs text-gray-400">Track for compliance</div>
                </div>
              </div>
            );
          })()}
        </>
      )}

      {activeTab === 'radar' && !isLoadingRisks && (
        <>
          {isLoadingCompliance ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <span className="ml-3 text-gray-600">Loading compliance data...</span>
            </div>
          ) : (
            <RegulatoryAlignmentRadar
              contracts={complianceData}
              onAreaClick={handleRadarAreaClick}
              onViewGaps={handleViewGaps}
              testId="regulatory-alignment-radar"
            />
          )}
        </>
      )}

      {activeTab === 'priority' && !isLoadingRisks && (
        <>
          {riskFindings.length === 0 && !riskError ? (
            <EmptyState
              type="no-risks"
              onRunAnalysis={() => setActiveTab('doc-compliance')}
              onViewContracts={() => onNavigate('/contracts')}
            />
          ) : (
            <RiskPriorityMatrix
              risks={riskFindings}
              onRiskClick={handlePriorityRiskClick}
              onViewInContract={handleViewInContract}
              testId="risk-priority-matrix"
            />
          )}
        </>
      )}

      {activeTab === 'doc-compliance' && (
        <RegulatoryComplianceView testId="doc-compliance-view" />
      )}
    </div>
  );
};

// Regulations page with detail view routing
const RegulationsPageWithDetail: React.FC<{ onNavigate: (path: string) => void }> = ({ onNavigate }) => {
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [selectedDoc, setSelectedDoc] = useState<RegulatoryDocument | null>(null);

  const handleViewDocument = (docId: string) => {
    setSelectedDocId(docId);
    // Load the document details
    fetchRegulatoryDocuments().then((docs) => {
      const doc = docs.find((d) => d.id === docId);
      if (doc) setSelectedDoc(doc);
    });
  };

  const handleBack = () => {
    setSelectedDocId(null);
    setSelectedDoc(null);
  };

  const handleRefresh = () => {
    if (selectedDocId) {
      fetchRegulatoryDocuments().then((docs) => {
        const doc = docs.find((d) => d.id === selectedDocId);
        if (doc) setSelectedDoc(doc);
      });
    }
  };

  if (selectedDoc) {
    return <RegulatoryDocDetail document={selectedDoc} onBack={handleBack} onRefresh={handleRefresh} />;
  }

  return <RegulationsPage onViewDocument={handleViewDocument} />;
};

const ReportsPage: React.FC = () => (
  <div className="space-y-6">
    <h2 className="text-2xl font-bold text-gray-900">Reports</h2>
    <p className="text-gray-500">Generate and download analysis reports.</p>
    <div className="bg-white rounded-lg shadow-md p-6">
      <p className="text-gray-400 text-center py-8">No reports generated yet</p>
    </div>
  </div>
);

// Chat Page with Contract RAG Chat Interface + Persistent History Sidebar
interface ChatPageProps {
  onNavigate: (path: string) => void;
}

function persistedToMessages(msgs: PersistedChatMessage[]): ChatMessage[] {
  return msgs.map((m) => ({
    id: m.id,
    role: m.role,
    content: m.content,
    responses: m.responses,
    comparisonAnalysis: m.comparisonAnalysis,
    timestamp: new Date(m.timestamp),
  }));
}

const ChatPage: React.FC<ChatPageProps> = ({ onNavigate }) => {
  const [chatContracts, setChatContracts] = useState<ChatContractOption[]>([]);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  // sidebarActiveId tracks the highlighted conversation in the sidebar.
  // It diverges from activeConversationId when a new conversation is created mid-chat
  // (we don't want to remount ContractChat, but we do want to highlight it in the sidebar).
  const [sidebarActiveId, setSidebarActiveId] = useState<string | null>(null);
  const [activeMessages, setActiveMessages] = useState<ChatMessage[]>([]);
  const [activeContractIds, setActiveContractIds] = useState<string[]>([]);
  const [activeDualMode, setActiveDualMode] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Load contracts on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetchContracts({ page: 1, limit: 100 });
        if (!cancelled) {
          setChatContracts(
            response.contracts.map((c) => ({
              id: c.id,
              name: c.name,
              state: c.state,
              status: c.status,
            })),
          );
        }
      } catch (err) {
        console.error('Failed to load contracts for chat:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load conversation list on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await listChatConversations();
        if (!cancelled) setConversations(result.conversations);
      } catch (err) {
        console.error('Failed to load conversations:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const refreshConversations = useCallback(async (query?: string) => {
    try {
      const result = await listChatConversations(query ? { q: query } : undefined);
      setConversations(result.conversations);
    } catch (err) {
      console.error('Failed to refresh conversations:', err);
    }
  }, []);

  const handleNewChat = useCallback(() => {
    setActiveConversationId(null);
    setSidebarActiveId(null);
    setActiveMessages([]);
    setActiveContractIds([]);
    setActiveDualMode(false);
  }, []);

  const handleSelectConversation = useCallback(async (id: string) => {
    try {
      const conv = await getChatConversation(id);
      setActiveConversationId(conv.id);
      setSidebarActiveId(conv.id);
      setActiveMessages(persistedToMessages(conv.messages));
      setActiveContractIds(conv.contractIds);
      setActiveDualMode(conv.dualMode);
    } catch (err) {
      console.error('Failed to load conversation:', err);
    }
  }, []);

  const handleDeleteConversation = useCallback(async (id: string) => {
    try {
      await deleteChatConversation(id);
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (activeConversationId === id) {
        handleNewChat();
      }
    } catch (err) {
      console.error('Failed to delete conversation:', err);
    }
  }, [activeConversationId, handleNewChat]);

  const handleConversationCreated = useCallback((conv: ConversationSummary) => {
    // Don't call setActiveConversationId here — that would change the `key` prop
    // on ContractChat, forcing a remount and losing the current chat state.
    // ContractChat already tracks the new ID internally via activeConvIdRef.
    // Only update sidebar highlighting.
    setSidebarActiveId(conv.id);
    setConversations((prev) => [conv, ...prev]);
  }, []);

  const handleMessagesUpdated = useCallback((_convId: string, _messages: PersistedChatMessage[]) => {
    // Update the timestamp in the sidebar list
    setConversations((prev) =>
      prev.map((c) =>
        c.id === _convId ? { ...c, updatedAt: new Date().toISOString() } : c,
      ).sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()),
    );
  }, []);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    refreshConversations(query);
  }, [refreshConversations]);

  return (
    <div className="h-[calc(100vh-4rem)] flex" data-testid="chat-page">
      <ChatHistorySidebar
        conversations={conversations}
        activeConversationId={sidebarActiveId}
        onSelectConversation={handleSelectConversation}
        onNewChat={handleNewChat}
        onDeleteConversation={handleDeleteConversation}
        onSearch={handleSearch}
        searchQuery={searchQuery}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed((prev) => !prev)}
        contracts={chatContracts}
      />
      <div className="flex-1 min-w-0">
        <ContractChat
          key={activeConversationId ?? 'new'}
          contracts={chatContracts}
          onNavigate={onNavigate}
          conversationId={activeConversationId}
          initialMessages={activeMessages}
          initialContractIds={activeContractIds}
          initialDualMode={activeDualMode}
          onConversationCreated={handleConversationCreated}
          onMessagesUpdated={handleMessagesUpdated}
          onNewChat={handleNewChat}
        />
      </div>
    </div>
  );
};

// Search Page with Natural Language Search Interface
interface SearchPageProps {
  onNavigate: (path: string) => void;
}

const SearchPage: React.FC<SearchPageProps> = ({ onNavigate }) => {
  const handleResultClick = (result: SearchResult) => {
    console.log('Result clicked:', result.id, result.contractName);
  };

  const handleViewInContext = (result: SearchResult) => {
    console.log('View in context:', result.id);
    // Navigate to the contract detail page
    onNavigate('/contracts');
  };

  return (
    <div className="space-y-6" data-testid="search-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900" data-testid="search-title">Search</h2>
          <p className="mt-1 text-sm text-gray-500" data-testid="search-subtitle">
            Search across all contracts using natural language queries
          </p>
        </div>
      </div>

      <NaturalLanguageSearch
        onResultClick={handleResultClick}
        onViewInContext={handleViewInContext}
        availableStates={['PR', 'FL', 'TX', 'CA', 'NY']}
        testId="search-interface"
      />
    </div>
  );
};

// Contract Detail Page
interface ContractDetailPageProps {
  contract: Contract;
  onBack: () => void;
  onNavigate: (path: string) => void;
  highlightClause?: string | null;
}

// Wrapper to pass navigation to ContractsPage
const ContractsPageWithNav: React.FC<{
  onViewContract: (contract: Contract) => void;
  onNavigate: (path: string) => void;
}> = ({ onViewContract, onNavigate }) => {
  return <ContractsPage onViewContract={onViewContract} onNavigate={onNavigate} />;
};

const ContractDetailPage: React.FC<ContractDetailPageProps> = ({
  contract,
  onBack,
  onNavigate,
  highlightClause,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAnalyzing] = useState(false);
  const [articles, setArticles] = useState<Article[]>([]);
  const [isLoadingStructure, setIsLoadingStructure] = useState(false);
  const [structureError, setStructureError] = useState<string | null>(null);
  const [activeDetailTab, setActiveDetailTab] = useState<'structure' | 'financial-risk' | 'contract-ambiguity' | 'deadline-clauses' | 'pipeline'>('structure');
  const [currentStatus, setCurrentStatus] = useState<ContractStatus>(contract.status);

  // Re-fetch enriched status from the status endpoint on mount
  useEffect(() => {
    let cancelled = false;
    getContractStatus(contract.id)
      .then((resp) => {
        if (!cancelled && resp.status) {
          setCurrentStatus(resp.status as ContractStatus);
        }
      })
      .catch((err) => {
        console.warn('Failed to refresh contract status:', err);
      });
    return () => { cancelled = true; };
  }, [contract.id]);

  // Fetch contract structure when component mounts or contract changes
  useEffect(() => {
    const loadContractDetail = async () => {
      // Only fetch if contract is in a state that has structure
      if (!['structured', 'embedded', 'linked', 'ready'].includes(currentStatus)) {
        return;
      }

      setIsLoadingStructure(true);
      setStructureError(null);

      try {
        const response = await fetchContractDetail(contract.id);

        // Transform API response to Article[] format expected by ArticleSectionTree
        const transformedArticles: Article[] = response.articles.map((article: ArticleFromAPI) => {
          let articleRiskCount = 0;

          const sections = article.sections.map(section => ({
            id: section.id,
            number: section.number,
            title: section.title,
            clauses: section.clauses?.map(clause => {
              // Extract risk/citation info from metadata
              const metadata = clause.metadata || {};
              const hasRisk = !!metadata.has_risk;
              if (hasRisk) articleRiskCount++;

              return {
                id: clause.id,
                number: clause.number || '',
                text: clause.text,
                hasCfrCitation: !!metadata.cfr_citation,
                hasRisk,
                riskSeverity: metadata.risk_severity as 'critical' | 'high' | 'medium' | 'low' | undefined,
              };
            }) || [],
          }));

          return {
            id: article.id,
            number: article.number,
            title: article.title,
            summary: article.summary || '',
            riskCount: articleRiskCount,
            sections,
          };
        });

        // Sort articles numerically (backend sorts as strings: "1","10","11"... instead of "1","2","3"...)
        transformedArticles.sort((a, b) => {
          const numA = parseInt(a.number, 10);
          const numB = parseInt(b.number, 10);
          if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
          return a.number.localeCompare(b.number);
        });
        setArticles(transformedArticles);
      } catch (error) {
        console.error('Failed to load contract structure:', error);
        setStructureError(error instanceof Error ? error.message : 'Failed to load structure');
      } finally {
        setIsLoadingStructure(false);
      }
    };

    loadContractDetail();
  }, [contract.id, currentStatus]);

  const handleProcess = () => {
    setIsProcessing(true);
    console.log('Processing contract:', contract.id);
    // Simulate processing
    setTimeout(() => {
      setIsProcessing(false);
    }, 3000);
  };

  const handleAnalyze = () => {
    console.log('Analyzing contract:', contract.id);
    // Navigate to analysis page
    onNavigate('/analysis');
  };

  const handleCompare = () => {
    console.log('Compare contract:', contract.id);
    onNavigate('/analysis');
  };

  const handleExport = () => {
    console.log('Export contract:', contract.id);
  };

  const handleRefresh = () => {
    console.log('Refresh contract:', contract.id);
  };

  const handleViewAnalysisResult = (result: AnalysisResult) => {
    console.log('View analysis result:', result.id);
    onNavigate('/analysis');
  };

  const handleRunAnalysis = () => {
    onNavigate('/analysis');
  };

  // Determine which articles to show based on status
  const showArticles = ['structured', 'embedded', 'linked', 'ready'].includes(currentStatus);

  const showFinancialRiskTab = ['structured', 'embedded', 'linked', 'ready'].includes(currentStatus);

  // Pipeline tab is useful at any stage past 'uploaded' — it shows R2 files, OCR output, and extracted articles
  const showPipelineTab = currentStatus !== 'uploaded';
  const showTabBar = showFinancialRiskTab || showPipelineTab;

  return (
    <div className="space-y-6" data-testid="contract-detail-page">
      {/* Metadata Header */}
      <ContractMetadataHeader contract={contract} statusOverride={currentStatus} onBack={onBack} />

      {/* Tab bar */}
      {showTabBar && (
        <div className="border-b border-gray-200" data-testid="detail-tab-bar">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveDetailTab('structure')}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeDetailTab === 'structure'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="tab-structure"
            >
              Contract Structure
            </button>
            {showFinancialRiskTab && (
            <button
              onClick={() => setActiveDetailTab('financial-risk')}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeDetailTab === 'financial-risk'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="tab-financial-risk"
            >
              Financial Risk Analysis
            </button>
            )}
            {showFinancialRiskTab && (
            <button
              onClick={() => setActiveDetailTab('contract-ambiguity')}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeDetailTab === 'contract-ambiguity'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="tab-contract-ambiguity"
            >
              Contract Ambiguity
            </button>
            )}
            {showFinancialRiskTab && (
            <button
              onClick={() => setActiveDetailTab('deadline-clauses')}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeDetailTab === 'deadline-clauses'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="tab-deadline-clauses"
            >
              Deadline Clauses
            </button>
            )}
            <button
              onClick={() => setActiveDetailTab('pipeline')}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeDetailTab === 'pipeline'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="tab-pipeline"
            >
              Extraction Pipeline
            </button>
          </nav>
        </div>
      )}

      {/* Tab content */}
      {activeDetailTab === 'pipeline' ? (
        <PipelineViewer contractId={contract.id} contractName={contract.name} />
      ) : activeDetailTab === 'financial-risk' && showFinancialRiskTab ? (
        <FinancialRiskAnalysisPanel contractId={contract.id} />
      ) : activeDetailTab === 'contract-ambiguity' && showFinancialRiskTab ? (
        <ContractAmbiguityAnalysisPanel contractId={contract.id} />
      ) : activeDetailTab === 'deadline-clauses' && showFinancialRiskTab ? (
        <DeadlineClausesPanel contractId={contract.id} />
      ) : (
      /* Main content grid */
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Actions */}
        <div className="space-y-6">
          <ContractActions
            contractStatus={currentStatus}
            onProcess={handleProcess}
            onAnalyze={handleAnalyze}
            onCompare={handleCompare}
            onExport={handleExport}
            onRefresh={handleRefresh}
            isProcessing={isProcessing}
            isAnalyzing={isAnalyzing}
          />
        </div>

        {/* Right column - Article Tree and Analysis Results */}
        <div className="lg:col-span-2 space-y-6">
          {showArticles ? (
            isLoadingStructure ? (
              <div className="bg-white rounded-lg shadow-md p-8 text-center" data-testid="loading-structure">
                <div className="animate-spin h-8 w-8 border-4 border-primary-600 border-t-transparent rounded-full mx-auto mb-3"></div>
                <p className="text-sm text-gray-500">Loading contract structure...</p>
              </div>
            ) : structureError ? (
              <div className="bg-white rounded-lg shadow-md p-8 text-center" data-testid="structure-error">
                <FileText className="h-12 w-12 text-red-300 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-gray-900 mb-1">
                  Failed to Load Structure
                </h3>
                <p className="text-sm text-red-500 mb-4">{structureError}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            ) : articles.length > 0 ? (
              <ArticleSectionTree
                articles={articles}
                onClauseClick={(clause, section, article) => {
                  console.log('Clause clicked:', clause.id, 'in', section.number, article.number);
                }}
                onArticleClick={(article) => {
                  console.log('Article clicked:', article.id);
                }}
                highlightClause={highlightClause}
              />
            ) : (
              <div className="bg-white rounded-lg shadow-md p-8 text-center" data-testid="no-articles">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-gray-900 mb-1">
                  No Structure Data
                </h3>
                <p className="text-sm text-gray-500">
                  The contract structure has not been extracted yet.
                </p>
              </div>
            )
          ) : (
            <div className="bg-white rounded-lg shadow-md p-8 text-center" data-testid="no-structure-message">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">
                Document Not Yet Processed
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Process the document to extract its structure and view articles, sections, and clauses.
              </p>
              {currentStatus === 'uploaded' && (
                <button
                  onClick={handleProcess}
                  disabled={isProcessing}
                  className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-primary-300"
                  data-testid="process-document-cta"
                >
                  {isProcessing ? 'Processing...' : 'Process Document'}
                </button>
              )}
            </div>
          )}

          {/* Analysis Results */}
          {contract.analysis_status && (
            <AnalysisResults
              results={mockAnalysisResults}
              onViewResult={handleViewAnalysisResult}
              onRunAnalysis={handleRunAnalysis}
            />
          )}
        </div>
      </div>
      )}
    </div>
  );
};

function App() {
  const [currentPath, setCurrentPath] = useState('/');
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [highlightClause, setHighlightClause] = useState<string | null>(null);

  const handleNavigate = (path: string) => {
    setCurrentPath(path);
    // Clear selected contract and highlight when navigating away
    if (path !== '/contracts/detail') {
      setSelectedContract(null);
      setHighlightClause(null);
    }
  };

  const handleViewContract = (contract: Contract) => {
    setSelectedContract(contract);
    setHighlightClause(null);
    setCurrentPath('/contracts/detail');
  };

  // Navigate to a contract with a specific clause highlighted
  const handleViewContractClause = (contractId: string, clauseReference: string) => {
    // Find the contract by ID from mock data (in production, this would fetch from API)
    const contract = mockContracts.find(c => c.id === contractId);
    if (contract) {
      setSelectedContract(contract);
      setHighlightClause(clauseReference);
      setCurrentPath('/contracts/detail');
    }
  };

  const handleBackToContracts = () => {
    setSelectedContract(null);
    setHighlightClause(null);
    setCurrentPath('/contracts');
  };

  const renderContent = () => {
    // Check for contract detail page
    if (currentPath === '/contracts/detail' && selectedContract) {
      return (
        <ContractDetailPage
          contract={selectedContract}
          onBack={handleBackToContracts}
          onNavigate={handleNavigate}
          highlightClause={highlightClause}
        />
      );
    }

    switch (currentPath) {
      case '/':
        return <DashboardContent onNavigate={handleNavigate} onViewContract={handleViewContract} />;
      case '/contracts':
        return <ContractsPageWithNav onViewContract={handleViewContract} onNavigate={handleNavigate} />;
      case '/chat':
        return <ChatPage onNavigate={handleNavigate} />;
      case '/search':
        return <SearchPage onNavigate={handleNavigate} />;
      case '/analysis':
        return <AnalysisPage />;
      case '/risks':
        return <RisksPage onNavigate={handleNavigate} onViewContractClause={handleViewContractClause} />;
      case '/regulations':
      case '/regulations/detail':
        return <RegulationsPageWithDetail onNavigate={handleNavigate} />;
      case '/reports':
        return <ReportsPage />;
      default:
        return <DashboardContent onNavigate={handleNavigate} onViewContract={handleViewContract} />;
    }
  };

  return (
    <DashboardLayout currentPath={currentPath.startsWith('/contracts') ? '/contracts' : (currentPath.startsWith('/regulations') ? '/regulations' : currentPath)} onNavigate={handleNavigate}>
      {renderContent()}
    </DashboardLayout>
  );
}

export default App;
